import PropTypes from "prop-types";
import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { IconFile, IconMail, IconSend } from "@tabler/icons";
import ActionButton from "src/components/shared/ActionButton";
import DayField from "src/components/shared/DayField";
import { daysSince, isoFromTyped } from "src/components/shared/days";
import { updateRequest } from "src/pages/request/actions";
import { casesApi } from "./api";
import { DecisionFields, focusFirstError, useShowWhenSet } from "./Decision";
import { decisionErrors, decisionForm, decisionPayload, decisionsKnown, failureWords, groundsFor, isSchengenCase, needsMigration } from "./decisions";
import { dateLong, money, statusLabel } from "./format";
import { lastConsulate, rememberConsulate } from "./workspace";
import { hasVisa, partnerName, paymentArranged, reportEmail, travellerLabel } from "./workflow";

// Recording the consulate's decision: the ONE way to set a case Approved or
// Refused (QA nadia-07, rania-15). Approved: what was granted, then each
// traveller's visa (optional, can come later), then a check: the payment,
// what happens and the email, saved with or without emailing. Refused: the
// grounds, then the check. The same dialog edits a decision already
// recorded and adds a visa to an approved case. It replaces "Change status
// > Approved", "Record the approval", "Approve and send visa" and the visa
// section's own upload, which overlapped.

// The email a refusal sends once decisions are recorded (API
// docs/visa-decisions.md): it points to the application page, not the bare
// status, and promises the call the case's next step asks for.
export const REFUSAL_EMAIL = "Your visa was refused. Your application page explains why and what you can do next. Someone from our team will call you.";

const STATUS_OF = { approved: "approved", refused: "rejected" };

// What the customer would be emailed, worked out the way the API words it:
// a visa saved with the approval says so ("Your France visa is approved"),
// a visa added later "is ready", otherwise the status email.
export function emailOf(caseData, { outcome, files, recordable }) {
  const country = String(caseData.destination || "").trim();
  const visaTitle = country ? `${country} visa` : "visa";
  const changes = caseData.status !== STATUS_OF[outcome];
  if (files > 0) {
    return caseData.status === "approved"
      ? {
          kind: "visa_ready",
          subject: `Your ${visaTitle} is ready`,
          body: `Your ${country ? `${country} ` : ""}visa has been added to your Safrati account. Sign in to safrati.ai to download it.`,
        }
      : {
          kind: "visa_approved",
          subject: `Your ${visaTitle} is approved`,
          body: `Good news: your ${country ? `${country} ` : ""}visa is approved. Sign in to safrati.ai to download it.`,
        };
  }
  if (!changes) return null;
  if (outcome === "refused" && recordable) {
    return { kind: "refusal", subject: `Application ${caseData.id}: Refused`, body: REFUSAL_EMAIL, link: true, message: true };
  }
  const words = statusLabel(STATUS_OF[outcome]);
  return { kind: "status", subject: `Application ${caseData.id}: ${words}`, body: `Your application is now: ${words}.`, message: true };
}

// A typed visa expiry, checked: "" when fine.
function expiryError(text) {
  const value = String(text || "").trim();
  if (!value) return "Add the expiry date printed on the visa.";
  const iso = isoFromTyped(value);
  if (!iso) return "Write a real date as day, month and year, like 20/10/2027.";
  if (daysSince(iso) > 0) return "This date has passed. Write the expiry printed on the visa.";
  return "";
}

function TravellerVisa({ caseData, traveller, onFile, value, onChange, error }) {
  const input = useRef(null);
  const label = travellerLabel(caseData, traveller);
  const replacing = onFile && value.replace;
  return (
    <Box sx={{ p: 1.5, border: 1, borderColor: error ? "error.main" : "divider", borderRadius: 2 }}>
      <Typography fontWeight={600} mb={1}>
        {label}
      </Typography>
      {onFile && !replacing ? (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" color="text.secondary">
            A visa is on file{onFile.expire_at ? `, expiring ${dateLong(onFile.expire_at)}` : ""}.
          </Typography>
          <ActionButton variant="text" onClick={() => onChange({ ...value, replace: true })} aria-label={`Replace the visa of ${label}`}>
            Replace it
          </ActionButton>
        </Stack>
      ) : (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "flex-start" }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <ActionButton startIcon={<IconFile size={16} />} onClick={() => input.current?.click()} aria-label={`${value.file ? "Choose another file" : "Choose the visa file"} for ${label}`}>
              {value.file ? "Choose another file" : "Choose the visa file"}
            </ActionButton>
            <input
              ref={input}
              type="file"
              hidden
              tabIndex={-1}
              aria-hidden="true"
              accept="application/pdf,image/*"
              onChange={(e) => {
                onChange({ ...value, file: e.target.files?.[0] || null });
                e.target.value = "";
              }}
            />
            {value.file ? (
              <Typography variant="body2" mt={0.75} noWrap title={value.file.name}>
                {value.file.name}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" mt={0.75}>
                {replacing ? "The visa on file stays until you choose a new one." : "Not chosen: you can add it later from the case."}
              </Typography>
            )}
          </Box>
          <DayField
            label={`Visa expiry date: ${label}`}
            showToday={false}
            value={value.expiry}
            onChange={(expiry) => onChange({ ...value, expiry })}
            error={error}
            hint="As printed on the visa."
          />
        </Stack>
      )}
    </Box>
  );
}

TravellerVisa.propTypes = {
  caseData: PropTypes.object.isRequired,
  traveller: PropTypes.object.isRequired,
  onFile: PropTypes.object,
  value: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
};

// start: { outcome: "approved" | "refused", step: "decision" | "visa" }.
// visas: each traveller's classic request (useTravellerVisas().byId), for
// the visa already on file. guard: the unpaid question (useUnpaidGuard).
export default function RecordDecision({ caseData, start = {}, visas = {}, guard = (run) => run(), unavailable = false, onClose, onDone, onUnavailable }) {
  const dispatch = useDispatch();
  const phone = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const partner = partnerName(caseData);
  const recordable = decisionsKnown(caseData) && !unavailable;
  const [outcome, setOutcome] = useState(start.outcome || null);
  const [remembered] = useState(() => lastConsulate(caseData));
  // What the product knows (round 2, nadia-22): the consulate last recorded
  // for this destination, and the product's length of stay. Each says so.
  const defaults = caseData.decision_defaults || {};
  const [forms, setForms] = useState(() => {
    const record = (o) => {
      const saved = caseData.decision?.outcome === o ? caseData.decision : null;
      const form = decisionForm({ outcome: o, decision: saved, consulate: remembered || defaults.consulate || "" });
      if (o === "approved" && !saved && !form.days && defaults.days) form.days = String(defaults.days);
      return form;
    };
    return { approved: record("approved"), refused: record("refused") };
  });
  const [step, setStep] = useState(start.outcome === "approved" && start.step === "visa" ? "visa" : "decision");
  const [tried, setTried] = useState(false);
  const [values, setValues] = useState(() => Object.fromEntries(caseData.travellers.map((t) => [t.request_id, { file: null, expiry: "", replace: false }])));
  const [visaErrors, setVisaErrors] = useState({});
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(null); // "quiet" | "email"
  const [progress, setProgress] = useState(null);
  const [failed, setFailed] = useState(null);
  const failure = useShowWhenSet(failed);
  const refs = useRef({});

  const form = outcome ? forms[outcome] : null;
  const errors = form && recordable ? decisionErrors(form) : {};
  const steps = outcome === "refused" ? ["decision", "save"] : ["decision", "visa", "save"];
  const at = steps.indexOf(step);
  const stepNames = { decision: "The decision", visa: "The visa", save: "Check and save" };
  const files = caseData.travellers.filter((t) => values[t.request_id]?.file);
  const statusChanges = !!outcome && caseData.status !== STATUS_OF[outcome];
  const email = outcome ? emailOf(caseData, { outcome, files: files.length, recordable }) : null;
  const unpaid = !paymentArranged(caseData);
  const total = caseData.payment?.amount ?? caseData.total;
  const onFile = (t) => (hasVisa(t, visas[t.request_id]) ? { expire_at: visas[t.request_id]?.expire_at || t.visa?.expire_at || null } : null);
  const nothingToSave = !statusChanges && !recordable && !files.length;

  const goTo = (next) => {
    setFailed(null);
    if (next === "visa") {
      // The visa's expiry is usually the decision's last valid day.
      const until = forms.approved.valid_until;
      if (until) setValues((all) => Object.fromEntries(Object.entries(all).map(([id, v]) => [id, v.expiry ? v : { ...v, expiry: until }])));
    }
    setStep(next);
  };

  const next = () => {
    if (step === "decision") {
      if (!outcome) {
        setTried(true);
        return;
      }
      if (Object.keys(errors).length) {
        setTried(true);
        setTimeout(() => focusFirstError(errors, refs), 0);
        return;
      }
    }
    if (step === "visa") {
      const found = {};
      caseData.travellers.forEach((t) => {
        const v = values[t.request_id];
        if (v.file) {
          const error = expiryError(v.expiry);
          if (error) found[t.request_id] = error;
        }
      });
      setVisaErrors(found);
      if (Object.keys(found).length) return;
    }
    goTo(steps[at + 1]);
  };

  // notify: email the customer (or have the partner tell the traveller).
  const run = async (notify) => {
    setFailed(null);
    const status = STATUS_OF[outcome];
    const decision = recordable ? decisionPayload(form) : null;
    let emailed;
    let partnerNotified;
    setProgress(0);
    if (statusChanges || decision) {
      try {
        // The status email goes only when no visa email follows.
        const statusEmail = notify && !files.length;
        const response = await casesApi.status(caseData.id, status, statusEmail ? message.trim() : "", dispatch, statusEmail, decision);
        if (statusEmail) emailed = response?.data?.emailed;
        partnerNotified = response?.data?.partner_notified;
        if (decision) rememberConsulate(caseData, decision.consulate);
      } catch (err) {
        setProgress(null);
        if (decision && needsMigration(err)) {
          onUnavailable?.();
          setFailed({ migration: true });
        } else setFailed({ reason: failureWords(err, "") });
        return false;
      }
    }
    // Then each visa chosen; the first one saved carries the one email.
    const notSaved = [];
    let emailDue = notify;
    for (let i = 0; i < files.length; i += 1) {
      const t = files[i];
      const v = values[t.request_id];
      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await updateRequest(
          {
            id: t.request_id,
            ...(caseData.status === "approved" ? {} : { status: "approved" }),
            visaFile: v.file,
            expireAt: isoFromTyped(v.expiry),
            notify: emailDue,
            quiet: true,
          },
          dispatch,
        );
        if (emailDue) {
          emailed = response?.data?.emailed;
          emailDue = false;
        }
      } catch {
        notSaved.push(t);
      }
      setProgress(i + 1);
    }
    setProgress(null);

    const what = [
      statusChanges ? `Case #${caseData.id} is ${statusLabel(status)}` : `Case #${caseData.id}: the decision is saved`,
      decision && statusChanges ? "the decision is recorded" : null,
      files.length - notSaved.length > 0
        ? partner
          ? `${files.length - notSaved.length === 1 ? "the visa goes" : `${files.length - notSaved.length} visas go`} to ${partner}`
          : `${files.length - notSaved.length === 1 ? "the visa is" : `${files.length - notSaved.length} visas are`} in the customer’s account`
        : null,
    ]
      .filter(Boolean)
      .join(", ");
    const told = partner
      ? partnerNotified === false
        ? ` The status did not reach ${partner}: it will be retried; tell them another way if it’s urgent.`
        : notify
          ? ` ${partner} tells the traveller.`
          : ` ${partner} gets it but won’t tell the traveller.`
      : !email
        ? " Nobody was emailed."
        : notify
          ? emailed === false
            ? ""
            : " The customer was emailed."
          : " No email was sent.";
    if (notSaved.length) {
      toast.warn(`${what}. The visa for ${notSaved.map((t) => travellerLabel(caseData, t)).join(" and ")} wasn’t saved: add it again from the case.${told}`);
    } else toast.success(`${what}.${told}`);
    if (!partner && email) await reportEmail({ asked: notify, emailed, caseId: caseData.id, what: `decision ${statusLabel(status)}`, dispatch });
    await onDone({ outcome, recorded: !!decision });
    return true;
  };

  const save = async (notify) => {
    setSaving(notify ? "email" : "quiet");
    // Approving or sending a visa on an unpaid application asks first.
    const result = outcome === "approved" && (statusChanges || files.length) ? await guard(() => run(notify)) : await run(notify);
    setSaving(null);
    return result;
  };

  const title = !statusChanges && caseData.decision && outcome && caseData.decision.outcome === outcome && step !== "visa" ? "Edit the decision" : step === "visa" && !statusChanges ? "Add the visa" : "Record the decision";

  return (
    <Dialog
      open
      fullScreen={phone}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="record-decision-title"
      aria-describedby="record-decision-step"
    >
      <DialogTitle id="record-decision-title" sx={{ pb: 0.5 }}>
        {title}
      </DialogTitle>
      <DialogContent>
        <Typography id="record-decision-step" variant="body2" color="text.secondary" mb={2}>
          Step {at + 1} of {steps.length}: {stepNames[step]}
        </Typography>
        {/* Said before anything is chosen, not only at the last click. */}
        {outcome === "approved" && unpaid ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Not paid: {money(total)} outstanding. The visa is usually sent once the payment is in; you’ll be asked to confirm before it’s saved.
          </Alert>
        ) : null}

        {step === "decision" ? (
          <Stack spacing={2.5}>
            <Box>
              <Typography id="record-decision-outcome" variant="subtitle2" fontWeight={600} mb={0.75}>
                What did the consulate decide?
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={outcome}
                onChange={(_, value) => {
                  if (!value) return;
                  setOutcome(value);
                  setTried(false);
                }}
                aria-labelledby="record-decision-outcome"
                sx={{
                  flexWrap: "wrap",
                  "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, px: 2.5, minHeight: 44 },
                  // The choice made is plain to see, not a faint grey.
                  "& .MuiToggleButton-root.Mui-selected": { bgcolor: "primary.main", color: "primary.contrastText", "&:hover": { bgcolor: "primary.dark" } },
                }}
              >
                <ToggleButton value="approved">Approved: visa granted</ToggleButton>
                <ToggleButton value="refused">Refused</ToggleButton>
              </ToggleButtonGroup>
              {tried && !outcome ? (
                <Typography role="alert" variant="body2" color="error.main" mt={0.5}>
                  Choose Approved or Refused.
                </Typography>
              ) : null}
            </Box>
            {outcome && recordable ? (
              <DecisionFields
                form={form}
                onChange={(value) => setForms((all) => ({ ...all, [outcome]: value }))}
                errors={tried ? errors : {}}
                refs={refs}
                idBase="record-decision"
                consulateRemembered={!!remembered && form.consulate === remembered}
                filled={{
                  consulate:
                    !remembered && defaults.consulate && form.consulate === defaults.consulate
                      ? `Filled in from case #${defaults.consulate_from}, the last decision for ${caseData.destination || "this destination"}. Change it if this letter says another.`
                      : undefined,
                  days: defaults.days && form.days === String(defaults.days) ? `Filled in from the product (${defaults.days} days). Check it against the visa.` : undefined,
                }}
                grounds={groundsFor(caseData, form.grounds)}
                schengen={isSchengenCase(caseData)}
              />
            ) : outcome ? (
              <Alert severity="info">Needs migration 023: this server doesn’t keep the decision’s details yet. The status changes; nothing else is recorded.</Alert>
            ) : null}
          </Stack>
        ) : null}

        {step === "visa" ? (
          <Stack spacing={1.5}>
            <Typography variant="body2">
              Choose each traveller’s visa file and the expiry printed on it. Leave one out to add it later; the case keeps asking for it.
            </Typography>
            {caseData.travellers.map((t) => (
              <TravellerVisa
                key={t.request_id}
                caseData={caseData}
                traveller={t}
                onFile={onFile(t)}
                value={values[t.request_id]}
                error={visaErrors[t.request_id]}
                onChange={(value) => {
                  setValues((all) => ({ ...all, [t.request_id]: value }));
                  setVisaErrors((all) => ({ ...all, [t.request_id]: undefined }));
                }}
              />
            ))}
          </Stack>
        ) : null}

        {step === "save" ? (
          <Stack spacing={2}>
            <Box component="section" aria-labelledby="record-decision-what">
              <Typography id="record-decision-what" variant="subtitle2" fontWeight={600} mb={0.5}>
                What happens
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}>
                <Typography component="li" variant="body2">
                  {statusChanges
                    ? caseData.travellers.length > 1
                      ? `All ${caseData.travellers.length} travellers move to ${statusLabel(STATUS_OF[outcome])}.`
                      : `The application moves to ${statusLabel(STATUS_OF[outcome])}.`
                    : `The status stays ${statusLabel(caseData.status)}.`}
                </Typography>
                {recordable ? (
                  <Typography component="li" variant="body2">
                    {outcome === "refused"
                      ? "The refusal is recorded: the customer’s page explains each ground, and the case asks the team to call them."
                      : "The decision is recorded, for the team’s records and the decision stats."}
                  </Typography>
                ) : null}
                {files.map((t) => (
                  <Typography component="li" variant="body2" key={t.request_id}>
                    Visa for {travellerLabel(caseData, t)}, expiring {dateLong(isoFromTyped(values[t.request_id].expiry))}
                    {onFile(t) ? ", replacing the one on file" : ""}: {partner ? `it goes to ${partner}.` : "it appears in the customer’s account."}
                  </Typography>
                ))}
                {outcome === "approved" && !files.length ? (
                  <Typography component="li" variant="body2">
                    No visa file now: the case asks for it until it’s added.
                  </Typography>
                ) : null}
              </Box>
            </Box>

            {email && !partner ? (
              <Box component="section" aria-labelledby="record-decision-email">
                <Typography id="record-decision-email" variant="subtitle2" fontWeight={600} mb={0.5}>
                  The email, if you choose to send it
                </Typography>
                <Box
                  component="dl"
                  sx={{ m: 0, p: 1.5, borderRadius: 2, bgcolor: "action.hover", display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 1.5, rowGap: 0.5 }}
                >
                  <Typography component="dt" variant="body2" color="text.secondary">
                    To
                  </Typography>
                  <Typography component="dd" variant="body2" sx={{ m: 0, fontWeight: 600, wordBreak: "break-word" }}>
                    {caseData.customer?.email || "No email address on file"}
                  </Typography>
                  <Typography component="dt" variant="body2" color="text.secondary">
                    Subject
                  </Typography>
                  <Typography component="dd" variant="body2" sx={{ m: 0, fontWeight: 600 }}>
                    {email.subject}
                  </Typography>
                  <Typography component="dt" variant="body2" color="text.secondary">
                    Message
                  </Typography>
                  <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                    “{email.body}”{email.link ? " With a link to their application." : ""}
                    {email.message && message.trim() ? " Then your message." : ""}
                  </Typography>
                </Box>
                {files.length > 0 && caseData.travellers.length > 1 ? (
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    One email for the whole application. safrati.ai shows one “{caseData.destination || "destination"} visa” per account; each traveller’s visa
                    stays on their own record.
                  </Typography>
                ) : null}
              </Box>
            ) : null}
            {partner && email ? (
              <Typography variant="body2" sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
                No email is sent from Safrati: <b>{partner}</b> receives the new status{files.length ? " and the visa" : ""}, and tells the traveller when you
                choose “Save and tell {partner}”.
              </Typography>
            ) : null}
            {email?.message ? (
              <TextField
                fullWidth
                multiline
                minRows={2}
                label={partner ? "Message for the traveller (optional)" : "Message in the email (optional)"}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                inputProps={{ maxLength: 1000 }}
              />
            ) : null}
          </Stack>
        ) : null}

        {progress != null && files.length > 0 ? (
          <Box mt={2} role="status" aria-live="polite">
            <LinearProgress variant="determinate" value={(progress / files.length) * 100} sx={{ height: 6, borderRadius: 3 }} />
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Saving visa {Math.min(progress + 1, files.length)} of {files.length}…
            </Typography>
          </Box>
        ) : null}

        {/* A failed save says what did not happen, here, not in a passing toast. */}
        <Box aria-live="assertive" ref={failure}>
          {failed?.migration ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Needs migration 023: this server can’t save the decision yet, so nothing was changed.
            </Alert>
          ) : failed ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              Nothing was changed and nobody was emailed. Try again.{failed.reason ? ` ${failed.reason}` : ""}
            </Alert>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, flexWrap: "wrap", "& > :not(style) ~ :not(style)": { ml: 0 } }}>
        <ActionButton variant="text" size="medium" onClick={onClose} disabled={!!saving}>
          Keep as it is
        </ActionButton>
        <Box sx={{ flex: 1 }} />
        {at > 0 ? (
          <ActionButton variant="outlined" size="medium" onClick={() => goTo(steps[at - 1])} disabled={!!saving}>
            Back
          </ActionButton>
        ) : null}
        {step !== "save" ? (
          <ActionButton variant="contained" size="medium" onClick={next}>
            Next
          </ActionButton>
        ) : !email ? (
          <ActionButton variant="contained" size="medium" busyLabel="Saving…" disabled={nothingToSave} onClick={() => save(false)}>
            Save the decision
          </ActionButton>
        ) : (
          <>
            <ActionButton
              variant="outlined"
              size="medium"
              sx={{ whiteSpace: "normal", maxWidth: "100%", textAlign: "center" }}
              disabled={saving === "email" || nothingToSave}
              busyLabel="Saving…"
              onClick={() => save(false)}
            >
              {partner ? "Save without telling the traveller" : "Save without emailing"}
            </ActionButton>
            <ActionButton
              variant="contained"
              size="medium"
              startIcon={partner ? <IconSend size={18} /> : <IconMail size={18} />}
              sx={{ whiteSpace: "normal", maxWidth: "100%", textAlign: "center" }}
              disabled={saving === "quiet" || nothingToSave || (!partner && !caseData.customer?.email)}
              busyLabel="Saving…"
              onClick={() => save(true)}
            >
              {partner ? `Save and tell ${partner}` : "Save and email the customer"}
            </ActionButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

RecordDecision.propTypes = {
  caseData: PropTypes.object.isRequired,
  start: PropTypes.shape({ outcome: PropTypes.oneOf(["approved", "refused"]), step: PropTypes.oneOf(["decision", "visa"]) }),
  visas: PropTypes.object,
  guard: PropTypes.func,
  unavailable: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  onUnavailable: PropTypes.func,
};
